import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Children, cloneElement, isValidElement, useCallback, useId, useMemo, useRef, useState, } from "react";
import { classes } from "../utils/classes";
import { mutableDOMRect } from "../utils/dom";
const Tooltip = (props) => {
    const { text, children, fontSize, maxWidth } = props;
    const id = useId();
    const tooltipRef = useRef(null);
    const [show, setShow] = useState(false);
    const [position, setPosition] = useState({ top: 0, left: 0 });
    const [above, setAbove] = useState(false);
    const [triangleMargin, setTriangleMargin] = useState();
    // On hover, compute position of tooltip and show it
    const handleEnter = useCallback((e) => {
        var _a;
        if (!tooltipRef.current)
            return;
        const target = e.currentTarget;
        const rect = mutableDOMRect(target.getBoundingClientRect());
        const offsetRect = (_a = target.offsetParent) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect();
        if (offsetRect) {
            rect.top -= offsetRect.top;
            rect.bottom -= offsetRect.top;
            rect.left -= offsetRect.left;
            rect.right -= offsetRect.left;
        }
        const tooltipRect = tooltipRef.current.getBoundingClientRect();
        if (rect.right + tooltipRect.width > window.innerWidth) {
            // If the tooltip box is past the right edge of the page, position it to the top
            setAbove(true);
            setPosition({
                top: rect.top - rect.height / 2 - tooltipRect.height,
                left: window.innerWidth / 2 - tooltipRect.width / 2,
            });
            // Calculate margin so triangle will be pointing to info icon
            setTriangleMargin(`0 ${window.innerWidth / 2 - rect.right + 5}px 0 0`);
        }
        else {
            // Position tooltip to the left
            setAbove(false);
            setPosition({
                top: rect.top + rect.height / 2 - tooltipRect.height / 2,
                left: rect.right + 10,
            });
            // Have the triangle be in the center of the tooltip
            setTriangleMargin("-5px 0 0");
        }
        setShow(true);
    }, []);
    // Compute the style of the tooltip
    const style = useMemo(() => ({
        opacity: show ? 1 : 0,
        top: position.top,
        left: position.left,
        maxWidth,
        fontSize,
    }), [show, position, maxWidth, fontSize]);
    // Add event listeners + refs to the children to show/hide tooltip
    const childrenWithProps = useMemo(() => Children.map(children, (child) => {
        if (!isValidElement(child))
            return child;
        return cloneElement(child, {
            onMouseEnter: handleEnter,
            onFocus: handleEnter,
            onMouseLeave: () => setShow(false),
            onBlur: () => setShow(false),
            "aria-describedby": id,
        });
    }), [children, handleEnter, id]);
    return (_jsxs(_Fragment, { children: [childrenWithProps, _jsxs("div", { className: "pointer-events-none fixed z-10 w-max rounded-lg bg-black/50 p-2 shadow-lg backdrop-blur-sm transition-opacity", ref: tooltipRef, style: style, id: id, role: "tooltip", children: [_jsx("div", { className: classes("absolute border-[5px] border-solid border-transparent", above
                            ? "top-full right-1/2 border-t-black/50"
                            : "top-1/2 right-full border-r-black/50"), style: { margin: triangleMargin } }), text] })] }));
};
export default Tooltip;
